import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AtelierPhotoLightbox from './AtelierPhotoLightbox';
import Icon from '../../common/Icon';

// Colonne gauche de l'atelier : "Mes souvenirs" — photos et souvenirs
// (textes) disponibles. Deux facons de placer un element dans un
// emplacement (cahier des charges §9) : glisser-deposer (draggable natif,
// aucune librairie necessaire pour une interaction aussi simple), ou
// cliquer pour le selectionner puis cliquer sur un emplacement disponible
// (alternative pour les appareils ou le drag & drop est peu pratique).
//
// Ajout direct (photos + textes) et suppression : reprend exactement les
// memes appels (uploadPhoto/addTextItem/deleteContentItem) que l'ancien
// assistant /composer (etape "Contenu") — l'atelier n'a plus besoin d'un
// detour par cet assistant pour un livre qui a deja son nombre de pages.
//
// Les miniatures utilisent toujours metadata.thumbnailUrl quand disponible
// (jamais l'original) — repli silencieux sur `url` pour les photos
// uploadees avant l'introduction des miniatures (voir storageService.js).

// Apercu AGRANDI d'une photo, au survol de sa vignette.
//
// Les vignettes de la bibliotheque font ~70 px : impossible d'y reconnaitre
// une photo avant de la glisser dans une page (retour utilisateur 2026-09-15 :
// « il faut pouvoir avoir un apercu des photos dans la bibliotheque pour voir
// la photo avant de la glisser »).
//
// Position FIXE calculee a partir de la vignette survolee, pas un simple
// positionnement relatif : la bibliotheque defile et rogne son contenu
// (overflow), un apercu pose dedans serait coupe. Il est donc rendu par-dessus
// tout, a droite de la vignette — et bascule a gauche quand il n'y a plus la
// place, pour ne jamais sortir de l'ecran.
//
// Survol SEULEMENT : sur telephone il n'existe pas, d'ou le bouton loupe qui
// l'accompagne — les deux repondent au meme besoin par deux chemins.
const APERCU_LARGEUR = 260;
const APERCU_MARGE = 12;

// « VOICI MES SOUVENIRS », PAS « VOICI UNE LISTE DE 72 FICHIERS »
// (refonte visuelle 2026-09-26, §3 : "une mosaique/grille de vignettes de
// taille visuelle homogene").
//
// La premiere version de cette mosaique (2026-09-26, meme jour) donnait a
// chaque vignette une taille DIFFERENTE (1, 2 ou 3 tuiles), pour rompre la
// monotonie d'une grille de fichiers. A l'usage, ca produisait l'inverse de
// l'effet cherche : certaines photos devenaient enormement plus hautes que
// les autres, ce qui EST precisement l'aspect "gestionnaire de fichiers
// desordonne" qu'on voulait fuir. Retour a des tuiles CARREES et
// HOMOGENES — voir .atelier-photo-cloud (BookAtelierLuxe.css) : une grille
// classique a colonnes fixes, sans variation de taille par photo.

function PhotoHoverPreview({ apercu }) {
  if (!apercu) return null;
  const placeADroite = apercu.rect.right + APERCU_MARGE + APERCU_LARGEUR <= window.innerWidth;
  const left = placeADroite
    ? apercu.rect.right + APERCU_MARGE
    : Math.max(APERCU_MARGE, apercu.rect.left - APERCU_MARGE - APERCU_LARGEUR);
  // Centre verticalement sur la vignette, sans jamais deborder en haut ni en bas.
  const top = Math.min(
    Math.max(APERCU_MARGE, apercu.rect.top + apercu.rect.height / 2 - APERCU_LARGEUR / 2),
    Math.max(APERCU_MARGE, window.innerHeight - APERCU_LARGEUR - APERCU_MARGE)
  );
  return (
    <div className="atelier-sidebar-hover-preview" style={{ left, top, width: APERCU_LARGEUR }}>
      <img src={apercu.url} alt="" />
    </div>
  );
}

function AtelierSidebar({
  photos,
  souvenirs,
  selectedItem,
  onSelectItem,
  onItemDragStart,
  onUploadPhotos,
  onDeleteItem,
  uploadingPhotos,
  uploadProgress,
  onDeleteAll,
  deletingAll,
  addError,
  // Pre-selection de l'onglet depuis le dashboard ("Ajouter mes photos"/
  // "Ajouter mes souvenirs" -> /atelier?tab=photos|souvenirs, voir
  // BookAtelierLuxe.js) — 'photos' par defaut sinon, comportement inchange.
  initialTab,
  // Set des item id deja places sur une page interieure quelconque du livre
  // (voir BookAtelierLuxe.js: usedItemIds) — affiche un badge "utilisee" +
  // assombrit legerement la vignette, pour eviter un doublon accidentel.
  // Purement informatif : reste cliquable/glissable normalement, une
  // reutilisation deliberee (ex. meme photo en debut et fin de livre) n'est
  // jamais bloquee.
  usedItemIds,
  // Un livre solo n a pas de contributeurs : ni filtres par personne, ni « Mots recus ».
  estSolo = false,
  // « Placer sur une page » d'un mot recu (refonte du collectif, phase 3) — voir BookAtelierLuxe.js.
  onPlaceText = null
}) {
  const { t } = useTranslation('atelier');
  // { url, rect } de la photo survolee, null sinon. `rect` est fige au moment
  // du survol : l'apercu ne suit pas la souris, il reste ancre a sa vignette.
  const [apercu, setApercu] = useState(null);
  // Photo ouverte en grand (bouton loupe). Chemin tactile, ou simplement pour
  // regarder la photo a sa vraie resolution.
  const [photoOuverte, setPhotoOuverte] = useState(null);
  const apercuTimer = useRef(null);

  // Petit delai avant d'afficher : sans lui, balayer la grille du regard fait
  // clignoter une dizaine d'apercus.
  const survoler = (event, item) => {
    if (item.kind !== 'photo') return;
    const rect = event.currentTarget.getBoundingClientRect();
    const url = item.metadata?.previewUrl || item.url;
    clearTimeout(apercuTimer.current);
    apercuTimer.current = setTimeout(() => {
      setApercu({ url, rect: { top: rect.top, left: rect.left, right: rect.right, height: rect.height } });
    }, 220);
  };

  const quitter = () => {
    clearTimeout(apercuTimer.current);
    setApercu(null);
  };

  // TROIS FAMILLES, PAS DEUX (2026-09-22).
  //
  // L'onglet « Souvenirs » melangeait deux choses qui n'ont rien a voir :
  // les textes qu'on ecrit soi-meme, et ce que les proches envoient. Et les
  // PHOTOS des proches, elles, atterrissaient parmi les siennes — « on ne
  // peut pas savoir qui a envoye quelle photo ».
  //
  //   « Mes photos » : les siennes.
  //   « Reçues »     : tout ce qui arrive par le lien de partage, photos ET
  //                    mots, avec le nom de qui les a envoyes. C'est le
  //                    coeur d'un album collectif, ca meritait sa place.
  //   « Souvenirs »  : les textes ecrits depuis la bibliotheque. N'apparait
  //                    QUE si le livre en contient deja.
  //
  // Le champ « Ecrire un souvenir... » disparait : on ecrit desormais
  // directement dans l'emplacement, sur la page (AtelierTextEditor). Ecrire
  // un texte sans savoir ou il ira produisait des souvenirs orphelins.
  // L'onglet reste pour ceux qui existent — 24 en base au moment du
  // changement, dont 8 deja poses : les faire disparaitre les rendrait
  // introuvables.
  const estContribution = (item) => item?.source === 'contribution';
  const mesPhotos = useMemo(() => photos.filter((item) => !estContribution(item)), [photos]);
  const mesSouvenirs = useMemo(() => souvenirs.filter((item) => !estContribution(item)), [souvenirs]);

  // LES PHOTOS DES PROCHES DANS L'ONGLET PHOTOS (refonte du collectif, phase 3, 2026-10-24) : l'ancien onglet « Recues »
  // separait tout ; desormais un seul onglet Photos, filtrable par personne (Toutes, Mes photos, un filtre par proche), une
  // pastille d'initiale sur chaque photo recue, et les mots recus juste en dessous, a placer sur une page en un clic.
  const photosRecues = useMemo(() => photos.filter(estContribution), [photos]);
  const motsRecus = useMemo(() => souvenirs.filter(estContribution), [souvenirs]);
  const avecProches = !estSolo && (photosRecues.length > 0 || motsRecus.length > 0);
  const nomDe = (item) => (item.metadata?.contributor_name || '').trim();
  const personnes = useMemo(() => {
    const parNom = new Map();
    photosRecues.forEach((item) => { const nom = nomDe(item); parNom.set(nom, (parNom.get(nom) || 0) + 1); });
    return [...parNom.entries()].map(([nom, nombre]) => ({ nom, nombre }));
  }, [photosRecues]);
  const [personne, setPersonne] = useState('toutes'); // 'toutes' | 'moi' | 'p:<nom>'
  const motsAffiches = personne === 'moi' ? [] : (personne.startsWith('p:') ? motsRecus.filter((m) => nomDe(m) === personne.slice(2)) : motsRecus);
  const ongletSouvenirsDisponible = mesSouvenirs.length > 0;

  const [activeTab, setActiveTab] = useState(initialTab === 'souvenirs' ? 'souvenirs' : 'photos');
  // Filtre a trois etats (refonte 2026-09-26, §3 : "filtre Toutes /
  // Disponibles / Utilisées"). Remplace la puce unique "Non utilisées" de
  // la premiere version — meme principe, un choix de plus. Local, non
  // persiste : repart a "Toutes" a chaque ouverture du tiroir, jamais un
  // filtre qu'on pourrait oublier avoir laisse actif.
  const [filtre, setFiltre] = useState('toutes'); // 'toutes' | 'disponibles' | 'utilisees'

  // Un onglet qui disparait (dernier element retire, passage en solo) ne
  // doit pas laisser la colonne sur du vide.
  useEffect(() => {
    if (activeTab === 'souvenirs' && !ongletSouvenirsDisponible) setActiveTab('photos');
  }, [activeTab, ongletSouvenirsDisponible]);
  // Le proche filtre n'a plus de photo (retire, photos supprimees) : retour a « Toutes ».
  useEffect(() => {
    if (personne.startsWith('p:') && !personnes.some((p) => p.nom === personne.slice(2))) setPersonne('toutes');
  }, [personne, personnes]);

  // CE QUI RESTE A PLACER D'ABORD, CE QUI EST DEJA PLACE EN BAS (2026-09-19).
  //
  // La bibliotheque d'un livre de 60 photos melange celles qu'on a deja
  // posees et celles qu'on cherche encore : le badge « utilisee » le disait
  // deja, mais il fallait balayer toute la grille pour trouver les
  // restantes. Le travail en cours est en haut, l'archive en bas.
  //
  // Tri STABLE (deux filtres, pas un sort) : a l'interieur de chaque groupe
  // l'ordre d'origine — celui du livre — est conserve tel quel, et poser une
  // photo ne fait donc que la deplacer en fin de liste, sans rebattre le
  // reste. Vaut aussi pour les souvenirs : meme badge, meme besoin.
  const items = useMemo(() => {
    let base;
    if (activeTab === 'souvenirs') base = mesSouvenirs;
    else if (!avecProches || personne === 'moi') base = mesPhotos;
    else if (personne.startsWith('p:')) base = photosRecues.filter((item) => nomDe(item) === personne.slice(2));
    else base = photos;
    if (!usedItemIds || usedItemIds.size === 0) return base;
    return [
      ...base.filter((item) => !usedItemIds.has(item.id)),
      ...base.filter((item) => usedItemIds.has(item.id))
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, mesPhotos, photos, photosRecues, personne, avecProches, mesSouvenirs, usedItemIds]);

  const nombreUtilises = useMemo(
    () => (usedItemIds ? items.filter((item) => usedItemIds.has(item.id)).length : 0),
    [items, usedItemIds]
  );

  // Ce qui s'affiche REELLEMENT : `items` reste la source complete (pour le
  // compte du filtre et "Tout supprimer"), ce tableau applique juste le
  // filtre en plus quand il est actif.
  const itemsAffiches = useMemo(() => {
    if (!usedItemIds || filtre === 'toutes') return items;
    if (filtre === 'disponibles') return items.filter((item) => !usedItemIds.has(item.id));
    return items.filter((item) => usedItemIds.has(item.id));
  }, [items, filtre, usedItemIds]);

  const handleDragStart = (event, item) => {
    event.dataTransfer.effectAllowed = 'copy';
    // `kind` inclus (retour utilisateur, 2026-09-11 : une photo deposee sur
    // un emplacement texte — ou l'inverse — n'etait bloquee nulle part cote
    // client, seulement signalee visuellement via .is-rejecting ; le
    // backend rejetait la sauvegarde ensuite avec un message brut) —
    // permet a atelierSlotInteractions.js de refuser reellement le depot
    // sans avoir besoin de re-resoudre l'item par son id.
    event.dataTransfer.setData('application/json', JSON.stringify({ itemId: item.id, kind: item.kind }));
  };

  const handlePhotoInputChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) onUploadPhotos(files);
    event.target.value = '';
  };

  return (
    <div className="atelier-sidebar">
      <div className="atelier-sidebar-tabs">
        <button
          type="button"
          className={`atelier-sidebar-tab ${activeTab === 'photos' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('photos')}
        >
          {estSolo ? t('sidebar.tabPhotosSolo') : (avecProches ? t('sidebar.tabPhotosAll') : t('sidebar.tabPhotosMine'))} ({avecProches ? photos.length : mesPhotos.length})
        </button>
        {ongletSouvenirsDisponible && (
          <button
            type="button"
            className={`atelier-sidebar-tab ${activeTab === 'souvenirs' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('souvenirs')}
          >
            {t('sidebar.tabMemories')} ({mesSouvenirs.length})
          </button>
        )}
      </div>

      {/* Le filtre remplace l'ancien texte permanent "X deja placees
          regroupees en bas" (§10 : reduire les textes d'aide permanents) —
          et redonne la main : plutot que de SUBIR le regroupement, on peut
          choisir de ne voir que ce qui manque encore, ou l'inverse.
          N'apparait que s'il y a quelque chose a filtrer. */}
      {activeTab === 'photos' && avecProches && (
        <div className="atelier-sidebar-filters atelier-sidebar-people" role="group" aria-label={t('sidebar.peopleLabel')}>
          {[
            { id: 'toutes', label: t('sidebar.peopleAll'), nombre: photos.length },
            { id: 'moi', label: t('sidebar.peopleMine'), nombre: mesPhotos.length },
            ...personnes.map((p) => ({ id: `p:${p.nom}`, label: p.nom || t('sidebar.peopleUnnamed'), nombre: p.nombre }))
          ].map((choix) => (
            <button
              key={choix.id}
              type="button"
              className={`atelier-sidebar-filter-chip ${personne === choix.id ? 'is-active' : ''}`}
              onClick={() => setPersonne(choix.id)}
              aria-pressed={personne === choix.id}
            >
              {choix.label} <b>{choix.nombre}</b>
            </button>
          ))}
        </div>
      )}

      {nombreUtilises > 0 && (
        <div className="atelier-sidebar-filters" role="group" aria-label={t('sidebar.filterLabel')}>
          {[
            { id: 'toutes', label: t('sidebar.filterAll') },
            { id: 'disponibles', label: t('sidebar.filterAvailable', { count: items.length - nombreUtilises }) },
            { id: 'utilisees', label: t('sidebar.filterUsed', { count: nombreUtilises }) }
          ].map((choix) => (
            <button
              key={choix.id}
              type="button"
              className={`atelier-sidebar-filter-chip ${filtre === choix.id ? 'is-active' : ''}`}
              onClick={() => setFiltre(choix.id)}
              aria-pressed={filtre === choix.id}
            >
              {choix.label}
            </button>
          ))}
        </div>
      )}

      <div className="atelier-sidebar-add">
        {activeTab === 'photos' ? (
          <>
            <label className={`atelier-sidebar-add-btn ${uploadingPhotos ? 'is-disabled' : ''}`}>
              {uploadingPhotos ? t('sidebar.uploading') : `+ ${t('sidebar.addPhotos')}`}
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoInputChange}
                disabled={uploadingPhotos}
                hidden
              />
            </label>
            {/* Avancement REEL (photos envoyees / total). Un lot de 40 photos
                prend plusieurs minutes : sans ce retour, rien ne distingue
                "ca travaille" de "c'est plante" (retour utilisateur
                2026-09-12). Les echecs eventuels sont comptes a part — le lot
                continue malgre eux. */}
            {uploadProgress && (
              <div className="atelier-upload-progress">
                <div className="atelier-upload-progress-head">
                  <span>{t('sidebar.uploadCount', { done: uploadProgress.done, total: uploadProgress.total })}</span>
                  {uploadProgress.failed > 0 && (
                    <span className="atelier-upload-progress-failed">{t('sidebar.uploadFailed', { count: uploadProgress.failed })}</span>
                  )}
                </div>
                <div className="atelier-upload-progress-bar">
                  <div
                    className="atelier-upload-progress-fill"
                    style={{ width: `${Math.round((uploadProgress.done / uploadProgress.total) * 100)}%` }}
                  />
                </div>
              </div>
            )}
          </>
        ) : null}
        {addError && <p className="atelier-sidebar-add-error">{addError}</p>}

        {/* Vider l'onglet courant. Volontairement DISCRET et en retrait (petit
            lien, pas un bouton), et jamais affiche quand il n'y a rien a
            supprimer : c'est une sortie de secours, pas une action courante.
            La confirmation, elle, est explicite (voir handleDeleteAll). */}
        {onDeleteAll && (activeTab === 'photos' ? photos : souvenirs).length > 0 && (
          <button
            type="button"
            className="atelier-sidebar-delete-all"
            onClick={() => onDeleteAll(activeTab === 'photos' ? 'photo' : 'texte')}
            disabled={deletingAll || uploadingPhotos}
          >
            {deletingAll
              ? t('sidebar.deleting')
              : t('sidebar.deleteAll', {
                count: (activeTab === 'photos' ? photos : souvenirs).length,
                kind: activeTab === 'photos' ? t('sidebar.deleteAllKindPhotos') : t('sidebar.deleteAllKindMemories')
              })}
          </button>
        )}
      </div>

      {items.length === 0 ? (
        activeTab === 'photos' ? (
          // Etat vide guide (maquette "Maquettes Bookipix", 2026-10-11) :
          // l'ancien texte seul ("Aucune photo pour le moment.") laissait
          // l'atelier "vide et muet" au tout premier contact. Purement
          // informatif — l'ajout reel se fait toujours via le bouton
          // ".atelier-sidebar-add-btn" juste au-dessus, deja cable a
          // onUploadPhotos : aucun nouvel input, aucun nouvel etat ici.
          <div className="atelier-sidebar-guided-empty">
            <span className="atelier-sidebar-guided-empty-icon"><Icon name="image" /></span>
            <p className="atelier-sidebar-guided-empty-title">{t('sidebar.guidedEmptyTitle')}</p>
            <p className="atelier-sidebar-guided-empty-hint">{t('sidebar.guidedEmptyHint')}</p>
            <ol className="atelier-sidebar-guided-empty-steps">
              <li>{t('sidebar.guidedEmptyStep1')}</li>
              <li>{t('sidebar.guidedEmptyStep2')}</li>
              {!estSolo && <li>{t('sidebar.guidedEmptyStep3')}</li>}
            </ol>
          </div>
        ) : (
          <p className="atelier-sidebar-empty">{t('sidebar.emptyMemories')}</p>
        )
      ) : itemsAffiches.length === 0 ? (
        // Le filtre cache tout : distinct du cas "rien du tout" — il y a
        // bien du contenu, juste pas dans l'etat filtre en ce moment.
        <p className="atelier-sidebar-empty">
          {filtre === 'utilisees' ? t('sidebar.emptyFilterUsed') : t('sidebar.emptyFilterAvailable')}
        </p>
      ) : (
        <>
        {/* GRILLE HOMOGENE, PAS UNE GRILLE DE FICHIERS (§3 : "mosaique/grille
            de vignettes de taille visuelle homogene") — voir
            .atelier-photo-cloud (BookAtelierLuxe.css). */}
        <div className="atelier-photo-cloud">
          {itemsAffiches.map((item) => {
            const isUsed = usedItemIds?.has(item.id);
            // RECONNAITRE CE QU'ON A RECU (2026-09-22).
            //
            // Une photo envoyee par un proche arrivait dans la bibliotheque
            // exactement comme les siennes : « je n'ai pas recu la photo
            // partagee — ou elle s'est peut-etre mise dans les autres
            // photos ». Elle etait bien la, indiscernable parmi 75.
            //
            // Le nom du contributeur n'est pas toujours connu (le lien de
            // partage ne l'exige pas) : on marque alors l'origine sans le
            // nommer, plutot que d'afficher un « de ? » qui n'apprend rien.
            const estRecue = item.source === 'contribution';
            const auteur = item.metadata?.contributor_name || null;
            return (
              // UN TEXTE N EST PAS UNE VIGNETTE (2026-09-22).
              //
              // Un souvenir etait pose dans le meme carre de 70 px qu une
              // photo, en corps 10, coupe apres quatre lignes : illisible,
              // donc inexploitable — « ca s'affiche en photo et donc
              // inexploitable ». Il prend desormais toute la largeur de la
              // colonne et se lit.
              <div
                key={item.id}
                className={`atelier-sidebar-item-wrap ${item.kind === 'texte' ? 'is-texte' : ''}`}
              >
                <button
                  type="button"
                  draggable
                  onDragStart={(event) => { quitter(); handleDragStart(event, item); if (onItemDragStart) onItemDragStart(); }}
                  onClick={() => onSelectItem(selectedItem?.id === item.id ? null : item)}
                  onMouseEnter={(event) => survoler(event, item)}
                  onMouseLeave={quitter}
                  className={`atelier-sidebar-item ${selectedItem?.id === item.id ? 'is-selected' : ''} ${isUsed ? 'is-used' : ''}`}
                  title={[
                    item.kind === 'photo' ? t('sidebar.titlePhoto') : item.text,
                    estRecue ? (auteur ? t('sidebar.sentBy', { name: auteur }) : t('sidebar.receivedViaLink')) : null,
                    isUsed ? t('sidebar.alreadyUsed') : null
                  ].filter(Boolean).join(' — ')}
                >
                  {item.kind === 'photo' ? (
                    <img src={item.metadata?.thumbnailUrl || item.url} alt="" loading="lazy" />
                  ) : (
                    <span className="atelier-sidebar-text-preview">{item.text}</span>
                  )}
                  {estRecue && item.kind === 'photo' && (
                    <span className="atelier-sidebar-item-by" aria-hidden="true">{auteur ? auteur.charAt(0).toUpperCase() : '♥'}</span>
                  )}
                  {estRecue && item.kind !== 'photo' && (
                    <span className="atelier-sidebar-item-recue-badge">
                      {auteur ? `♥ ${auteur}` : t('sidebar.receivedBadgeAnonymous')}
                    </span>
                  )}
                  {/* Point discret plutot qu'un bandeau de texte (§5 :
                      "sous forme discrete... petit badge, etat visuel").
                      L'information complete reste dans le title ci-dessus. */}
                  {isUsed && <span className="atelier-sidebar-item-used-dot" aria-hidden="true" />}
                </button>
                {/* Loupe : voir la photo en grand AVANT de la placer. Double
                    emploi assume avec l'apercu au survol — celui-ci n'existe
                    pas sur telephone, et regarder une photo a sa vraie
                    resolution reste utile meme a la souris. */}
                {item.kind === 'photo' && (
                  <button
                    type="button"
                    className="atelier-sidebar-item-zoom"
                    onClick={(event) => {
                      event.stopPropagation();
                      quitter();
                      // Version d'ecran (1600 px) et non l'original : sur un
                      // ecran courant la difference ne se voit pas, et
                      // l'original pese cinq fois plus (2026-09-20).
                      setPhotoOuverte(item.metadata?.previewUrl || item.url);
                    }}
                    aria-label={t('sidebar.viewLarge')}
                    title={t('sidebar.viewLarge')}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                      <circle cx="10.5" cy="10.5" r="6.5" />
                      <path d="M15.5 15.5 21 21" />
                      <path d="M10.5 7.5v6M7.5 10.5h6" />
                    </svg>
                  </button>
                )}
                <button
                  type="button"
                  className="atelier-sidebar-item-remove"
                  onClick={(event) => { event.stopPropagation(); onDeleteItem(item.id); }}
                  aria-label={t('sidebar.remove')}
                  title={t('sidebar.remove')}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
        </>
      )}

      {activeTab === 'photos' && avecProches && motsAffiches.length > 0 && (
        <section className="atelier-sidebar-mots" aria-label={t('sidebar.wordsReceived', { count: motsAffiches.length })}>
          <h4>{t('sidebar.wordsReceived', { count: motsAffiches.length })}</h4>
          {motsAffiches.map((mot) => {
            const place = usedItemIds?.has(mot.id);
            return (
              <div key={mot.id} className={`atelier-sidebar-mot ${place ? 'is-used' : ''}`}>
                <q>{String(mot.text || '').length > 280 ? `${String(mot.text).slice(0, 277)}…` : mot.text}</q>
                <small>{nomDe(mot) || t('sidebar.peopleUnnamed')}</small>
                {place ? (
                  <span className="atelier-sidebar-mot-done">{t('sidebar.wordPlaced')}</span>
                ) : onPlaceText && (
                  <button type="button" className="atelier-sidebar-mot-place" onClick={() => onPlaceText(mot)}>{t('sidebar.placeWord')}</button>
                )}
              </div>
            );
          })}
        </section>
      )}

      {/* Rendus en DERNIER et en position fixe : la bibliotheque defile et
          rogne son contenu, un apercu pose dedans serait coupe. */}
      <PhotoHoverPreview apercu={apercu} />
      <AtelierPhotoLightbox url={photoOuverte} onClose={() => setPhotoOuverte(null)} />
    </div>
  );
}

export default AtelierSidebar;
